import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CalendarClock,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isoDay = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export default function ProCalendar() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(true);
  const [cursor, setCursor] = useState(() => new Date());

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/workspace/jobs`);
      setJobs(data);
    } catch {
      /* ignore */
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  if (loading || busy) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D1421] text-[#C9C6C6]">
        <Loader2 className="w-5 h-5 animate-spin text-[#0E93EC] mr-2" /> Loading calendar…
      </div>
    );
  }
  if (!user) return <Navigate to="/workspace/login" replace />;

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startPad = new Date(year, month, 1).getDay();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const scheduled = jobs.filter((j) => j.schedule?.scheduled_date);
  const unscheduled = jobs.filter((j) => !j.schedule?.scheduled_date && j.status !== "completed");
  const byDay = {};
  scheduled.forEach((j) => {
    byDay[j.schedule.scheduled_date] = byDay[j.schedule.scheduled_date] || [];
    byDay[j.schedule.scheduled_date].push(j);
  });

  const shift = (n) => setCursor(new Date(year, month + n, 1));

  return (
    <div className="min-h-screen bg-[#0D1421]" data-testid="pro-calendar-page">
      <header className="border-b border-[#C9C6C6]/10 bg-[#010204] sticky top-0 z-30">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/workspace" className="p-2 text-[#C9C6C6] hover:text-[#EEEEEF]" data-testid="calendar-back">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#0E93EC] font-semibold">Your schedule</div>
              <div className="font-editorial text-lg text-[#EEEEEF] tracking-tight">Job Calendar</div>
            </div>
          </div>
          <img src="/brand/TPWH_Logo_Transparent.png" alt="The Pros Work Hub" className="h-9 w-auto" />
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-5">
          <button onClick={() => shift(-1)} className="p-2 border border-[#C9C6C6]/20 text-[#EEEEEF] hover:border-[#0E93EC]"
            data-testid="calendar-prev" aria-label="Previous month">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="font-editorial text-2xl font-light text-[#EEEEEF] tracking-tight" data-testid="calendar-month">
            {cursor.toLocaleString("en-US", { month: "long", year: "numeric" })}
          </div>
          <button onClick={() => shift(1)} className="p-2 border border-[#C9C6C6]/20 text-[#EEEEEF] hover:border-[#0E93EC]"
            data-testid="calendar-next" aria-label="Next month">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-[#C9C6C6] mb-1">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
          {cells.map((d, i) => {
            const key = d ? isoDay(year, month, d) : null;
            const dayJobs = key ? byDay[key] || [] : [];
            return (
              <div key={i}
                className={`min-h-[70px] sm:min-h-[90px] p-1.5 border ${
                  d ? "border-[#C9C6C6]/10 bg-[#010204]" : "border-transparent"
                }`}
                data-testid={key ? `cal-day-${key}` : undefined}>
                {d && (
                  <>
                    <div className="text-[11px] text-[#C9C6C6] mb-1">{d}</div>
                    <div className="space-y-1">
                      {dayJobs.map((j) => (
                        <button key={j.id}
                          onClick={() => navigate(`/workspace/jobs/${j.id}`)}
                          className="w-full text-left bg-[#0E93EC] hover:bg-[#0E2F5F] text-white text-[10px] leading-tight px-1.5 py-1 truncate transition-colors"
                          data-testid={`cal-job-${j.id}`}
                          title={j.lead?.service_type || "Job"}>
                          {j.schedule.time_window ? `${j.schedule.time_window} · ` : ""}{j.lead?.service_type || "Job"}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {unscheduled.length > 0 && (
          <div className="mt-8 border border-[#C9C6C6]/10 bg-[#010204] p-4 sm:p-5" data-testid="unscheduled-jobs">
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6] mb-3 flex items-center gap-2">
              <CalendarClock className="w-3.5 h-3.5 text-[#0E93EC]" /> Accepted jobs awaiting a date
            </div>
            <div className="space-y-2">
              {unscheduled.map((j) => (
                <div key={j.id} className="flex items-center justify-between gap-3 border border-[#C9C6C6]/10 bg-[#0D1421] px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="text-sm text-[#EEEEEF] font-medium truncate">
                      {j.lead?.service_type || "Job"} · {j.lead?.name || ""}
                    </div>
                    <div className="text-[11px] text-[#C9C6C6] font-mono-alt">W/O {j.invoice_number}</div>
                  </div>
                  <Link to={`/workspace/jobs/${j.id}`}
                    className="shrink-0 text-[11px] uppercase tracking-[0.2em] font-semibold bg-[#0E93EC] hover:bg-[#0E2F5F] text-white px-3 py-2 transition-colors"
                    data-testid={`schedule-job-${j.id}`}>
                    Schedule
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {scheduled.length === 0 && unscheduled.length === 0 && (
          <div className="text-center py-16 text-[#C9C6C6]">
            <CalendarClock className="w-8 h-8 mx-auto mb-3 text-[#0E93EC]" />
            <p className="text-sm">No accepted jobs yet. Win a job and schedule it — it'll appear here.</p>
          </div>
        )}
      </main>
    </div>
  );
}
